/**
 * ThemeContext · v2.7.43 · 3-way theme controller (light / dark / system)
 * ═════════════════════════════════════════════════════════════════════
 * Replaces the prior v2.6.7 dark-only lock. Exposes the same `useTheme`
 * surface so all existing imports keep working — now `setTheme` accepts
 * "light", "dark", or "system" (was a no-op).
 *
 *  • Persists choice to localStorage `admin-theme-v1`
 *  • `system` mode listens to `prefers-color-scheme: dark` and re-applies
 *    automatically when the OS preference toggles.
 *  • Sets `<html data-theme="light|dark">` so the global override
 *    stylesheet (`/src/styles/theme-light.css`) can remap surface colors.
 *  • Exports `bootThemeBeforePaint()` for use in `index.js` to avoid
 *    first-paint flash.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "admin-theme-v1";
const VALID = ["light", "dark", "system"];
const ThemeContext = createContext(null);

function readStored() {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "system";
  } catch { return "system"; }
}

function systemPref() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyToDoc(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  // Tailwind dark: modifier still works on the same class
  if (resolved === "dark") {
    root.classList.add("dark", "theme-dark");
    root.classList.remove("theme-light");
  } else {
    root.classList.remove("dark", "theme-dark");
    root.classList.add("theme-light");
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#09090b" : "#fafafa");
}

// Used in index.js BEFORE React mounts so first paint has correct theme.
export function bootThemeBeforePaint() {
  const stored = readStored();
  applyToDoc(stored === "system" ? systemPref() : stored);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStored());
  const [resolved, setResolved] = useState(() => (theme === "system" ? systemPref() : theme));

  // Apply on theme change
  useEffect(() => {
    const r = theme === "system" ? systemPref() : theme;
    setResolved(r);
    applyToDoc(r);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* private mode */ }
  }, [theme]);

  // OS-preference listener — only relevant when theme === "system"
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r = mq.matches ? "dark" : "light";
      setResolved(r);
      applyToDoc(r);
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [theme]);

  const setTheme = useCallback((next) => { if (VALID.includes(next)) setThemeState(next); }, []);
  const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  const value = useMemo(() => ({
    theme,
    resolved,
    isDark: resolved === "dark",
    setTheme,
    toggle,
  }), [theme, resolved, setTheme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { theme: "dark", resolved: "dark", isDark: true, setTheme: () => {}, toggle: () => {} };
  return ctx;
}
